import { useTranslation } from '../../hooks/useTranslation';
import { tokens } from '../../theme';
import { formatCNPJ } from '../redscore';
import {
  Box,
  Modal,
  Typography,
  useTheme,
  IconButton,
  Button,
  Grid,
  CircularProgress,
  TextField,
} from '@mui/material';
import FinancialPendenciesTable from '../../components/FinancialPendenciesTable';
import CloseIcon from '@mui/icons-material/Close';
import { formatMoney, isCPF } from '../../utils';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { HOST_API } from '../../config/hosts';
import { useCookiesLogin } from '../../config/cookies';
import CreditAnalysisSection from '../../components/CreditAnalysisSection';
import { ScoreDescription } from '../../components/ScoreDescription';

const InfoField = ({ label, value, xs = 12, md = 6 }) => (
  <Grid item xs={xs} md={md}>
    <Typography component="div" sx={{ fontSize: '14px', lineHeight: 1.6 }}>
      <Box component="span" sx={{ color: '#2c2c2c', fontWeight: 600 }}>
        {label}:
      </Box>{' '}
      <Box component="span" sx={{ color: '#6b6b6b', fontWeight: 400 }}>
        {value}
      </Box>
    </Typography>
  </Grid>
);

const InfoSection = ({ title, children }) => (
  <Box sx={{ mt: 2, p: 2, bgcolor: '#EEF0F3', borderRadius: '8px' }}>
    <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
      {title}
    </Typography>
    <Grid container spacing={2}>
      {children}
    </Grid>
  </Box>
);

const AddressItem = ({ address }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();

  return (
    <Box sx={{ mb: 2, pl: 2, borderLeft: `2px solid ${colors.greenAccent[500]}` }}>
      <Typography variant="body1" sx={{ color: colors.grey[300] }}>
        {address.addressMain}, {address.number}
        {address.complement && ` - ${address.complement}`}
      </Typography>
      <Typography variant="body2" sx={{ color: colors.grey[300] }}>
        {address.neighborhood} - {address.city}/{address.uf}, CEP: {address.zipcode}
      </Typography>
      {address.type && (
        <Typography variant="body2" sx={{ color: colors.grey[400], fontStyle: 'italic' }}>
          {t('consultation.type')}:{' '}
          {address.type === 'R' ? t('consultation.residential') : address.type}
        </Typography>
      )}
    </Box>
  );
};

const HistoryModal = ({ open, handleClose, row, loading }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { cookies } = useCookiesLogin();
  const { t } = useTranslation();

  const [observation, setObservation] = useState('');

  const valueOrDefault = (value, defaultText = t('messages.noInformation')) => {
    if (value === null || value === undefined || value === '') return defaultText;
    return value;
  };

  const hasAddress = (addresses) => Array.isArray(addresses) && addresses.length > 0;

  useEffect(() => {
    setObservation(row?.observation ?? '');
  }, [row]);

  if (!row) return null;

  const c = row.consult ?? {};
  const isPerson = isCPF(row.customerData?.taxvar);
  const addresses = c.addresses ?? [];
  const emails = c.emails ?? [];
  const phones = c.phones ?? [];

  const score = c.score ?? parseFloat(row.score) ?? 0;
  const eventProbability = c.event_probability ?? 0;

  const taxIdStatus = c.tax_id_status;
  const taxRegime = c.tax_regime;
  const riskScreening = c.risk_screening;
  const birthDate = c.birthDate ?? c.birth_date;
  const motherName = c.motherName ?? c.mother_name;
  const taxIdOrigin = c.taxIdOrigin ?? c.tax_id_origin;
  const tradeName = c.tradeName ?? c.trade_name;
  const companySize = c.companySize ?? c.company_size;
  const openingDate = c.openingDate ?? c.opening_date;
  const capital = c.capital;
  const operabilityIndicator = c.operabilityIndicator ?? c.operability_indicator;

  const protestOccurrences = c.protest?.occurrences ?? 0;
  const judicialActions = c.judicialAction?.occurrences ?? 0;
  const financialRestrictions = c.financialRestrictions?.occurrences ?? 0;
  const bankruptcies = c.bankruptcies?.occurrences ?? 0;
  const blockedByDataOwner = c.blockedByDataOwner ?? false;

  const financialPendenciesDetails =
    row.financialPendenciesDetails ||
    c.financialPendencies?.financialPendenciesDetails ||
    c.financialPendencies?.financialPendenciessDetails ||
    [];

  const financialPendencies =
    financialPendenciesDetails.length > 0
      ? financialPendenciesDetails.length
      : (c.financialPendencies?.occurrences ?? 0);

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    maxWidth: 900,
    maxHeight: '90vh',
    bgcolor: '#fff',
    border: `2px solid ${colors.greenAccent[500]}`,
    boxShadow: `0 8px 32px rgba(0, 0, 0, 0.3)`,
    borderRadius: 2,
    outline: 'none',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  };

  const handleSaveObservation = async () => {
    try {
      const { token } = cookies.AUTH_TOKEN;
      const res = await fetch(`${HOST_API}/v1/order/${row.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ observation }),
      });
      if (!res.ok) throw new Error(`Erro HTTP: ${res.status}`);
      alert(t('messages.observationSavedSuccess'));
    } catch (error) {
      console.error('Erro ao salvar observação', error);
      alert(t('messages.errorSavingObservation'));
    }
  };

  if (loading)
    return (
      <Box sx={{ textAlign: 'center', py: 10 }}>
        <CircularProgress sx={{ color: colors.greenAccent[400], mb: 2 }} />
        <Typography sx={{ color: colors.grey[100] }}>{t('messages.loadingClientData')}</Typography>
      </Box>
    );

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalStyle}>
        <Box sx={{ pt: 4, pb: 2 }} px={{ xs: 2, md: 4 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: 2,
            }}
          >
            <Typography
              variant="h4"
              component="h2"
              sx={{ color: colors.greenAccent[500], fontWeight: 600 }}
            >
              {t('consultation.consultDetails')}
            </Typography>
            <IconButton
              onClick={handleClose}
              sx={{
                color: colors.grey[100],
                mt: -1,
                '&:hover': { color: colors.greenAccent[500], bgcolor: 'transparent' },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Button
            variant="contained"
            onClick={() => {
              const printContent = document.getElementById('modal-content');
              if (printContent) {
                const originalContent = document.body.innerHTML;
                document.body.innerHTML = printContent.innerHTML;
                window.print();
                document.body.innerHTML = originalContent;
                window.location.reload();
              }
            }}
            sx={{
              bgcolor: colors.greenAccent[600],
              color: colors.grey[100],
              fontWeight: 700,
              '&:hover': { bgcolor: colors.greenAccent[700] },
            }}
          >
            {t('buttons.print')}
          </Button>
        </Box>

        <Box id="modal-content" sx={{ flex: 1, overflow: 'auto', py: 2 }} px={{ xs: 2, md: 4 }}>
          <Box>
            <Typography sx={{ fontSize: '16px', fontWeight: 700, mb: 1 }}>
              {row?.customerData?.full_name?.toUpperCase()}
            </Typography>
            <Typography sx={{ color: colors.grey[700] }}>
              {formatCNPJ(row?.customerData?.taxvar)} | {t('consultation.agroscore')}:{' '}
              <span
                style={{ fontFamily: 'Ubuntu', fontSize: '18px', fontWeight: 700, color: '#000' }}
              >
                {Math.floor(score)}
              </span>
            </Typography>
            <Typography sx={{ fontSize: '12px', color: colors.grey[700] }}>
              {t('consultation.consultedOn')}: {dayjs(row.created_at).format('DD/MM/YYYY HH:mm')}
            </Typography>
          </Box>

          <InfoSection title={t('consultation.infoCredits')}>
            <CreditAnalysisSection
              score={score}
              riskPercentage={eventProbability * 100}
              riskLabel={
                isPerson ? t('consultation.percentageRisk') : t('consultation.defaultProbability')
              }
            />
          </InfoSection>

          <InfoSection title={t('consultation.basicInfo')}>
            <InfoField
              label={t('consultation.cadastralSituation')}
              value={valueOrDefault(taxIdStatus)}
            />
            <InfoField label={t('consultation.taxRegime')} value={valueOrDefault(taxRegime)} />
            <InfoField
              label={t('consultation.riskClassification')}
              value={valueOrDefault(riskScreening)}
            />
            <InfoField
              label={t('consultation.defaultRisk')}
              value={`${(eventProbability * 100).toFixed(2)} %`}
            />

            {isPerson ? (
              <>
                <InfoField label={t('consultation.birthDate')} value={valueOrDefault(birthDate)} />
                <InfoField
                  label={t('consultation.motherName')}
                  value={valueOrDefault(motherName)}
                />
                <InfoField
                  label={t('consultation.cpfOrigin')}
                  value={valueOrDefault(taxIdOrigin)}
                />
              </>
            ) : (
              <>
                <InfoField label={t('consultation.tradeName')} value={valueOrDefault(tradeName)} />
                <InfoField
                  label={t('consultation.companySize')}
                  value={valueOrDefault(companySize)}
                />
                <InfoField
                  label={t('consultation.openingDate')}
                  value={valueOrDefault(openingDate)}
                />
                <InfoField
                  label={t('consultation.socialCapital')}
                  value={capital ? formatMoney(capital) : valueOrDefault(null)}
                />
                <InfoField
                  label={t('consultation.operabilityIndicator')}
                  value={valueOrDefault(operabilityIndicator)}
                />
              </>
            )}
          </InfoSection>

          <InfoSection title={t('consultation.financialData')}>
            <InfoField
              label={t('consultation.protests')}
              value={valueOrDefault(protestOccurrences, '0')}
            />
            <InfoField
              label={t('consultation.judicialActions')}
              value={valueOrDefault(judicialActions, '0')}
            />
            <InfoField
              label={t('consultation.financialRestrictions')}
              value={valueOrDefault(financialRestrictions, '0')}
            />
            <InfoField
              label={t('consultation.bankruptcies')}
              value={valueOrDefault(bankruptcies, '0')}
            />
            <InfoField
              label={t('consultation.financialPendencies')}
              value={valueOrDefault(financialPendencies, '0')}
            />
            <InfoField
              label={t('consultation.blockedByOwner')}
              value={blockedByDataOwner ? t('consultation.yes') : t('consultation.no')}
            />
          </InfoSection>

          {financialPendenciesDetails.length > 0 && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: colors.primary[500],
                borderRadius: 2,
              }}
            >
              <Typography variant="h4" gutterBottom color={colors.greenAccent[400]}>
                {t('consultation.financialPendenciesDetails')}
              </Typography>
              <FinancialPendenciesTable details={financialPendenciesDetails} />
            </Box>
          )}

          <InfoSection title={t('consultation.understandAgroScore')}>
            <ScoreDescription />
          </InfoSection>

          <InfoSection title={t('consultation.contactInfo')}>
            <Box sx={{ p: 2 }}>
              {hasAddress(addresses) ? (
                <>
                  <Typography variant="h6" sx={{ mb: 1, color: colors.grey[100] }}>
                    {t('consultation.addresses')}:
                  </Typography>
                  {addresses.map((address, index) => (
                    <AddressItem key={index} address={address} />
                  ))}
                </>
              ) : (
                <Typography variant="body1" sx={{ color: colors.grey[300] }}>
                  {t('consultation.noAddressAvailable')}
                </Typography>
              )}

              {emails.length > 0 ? (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1, color: colors.grey[100] }}>
                    {t('consultation.emails')}:
                  </Typography>
                  {emails.map((email, i) => (
                    <Typography key={i} variant="body1" sx={{ color: colors.grey[300] }}>
                      {email}
                    </Typography>
                  ))}
                </Box>
              ) : (
                <Typography variant="body1" sx={{ color: colors.grey[300], mb: 3 }}>
                  {t('consultation.noEmailAvailable')}
                </Typography>
              )}

              {phones.length > 0 ? (
                <Box>
                  <Typography variant="h6" sx={{ mb: 1, color: colors.grey[100] }}>
                    {t('consultation.phones')}:
                  </Typography>
                  {phones.map((phone, i) => (
                    <Typography key={i} variant="body1" sx={{ color: colors.grey[300] }}>
                      {phone}
                    </Typography>
                  ))}
                </Box>
              ) : (
                <Typography variant="body1" sx={{ color: colors.grey[300] }}>
                  {t('consultation.noPhoneAvailable')}
                </Typography>
              )}
            </Box>
          </InfoSection>

          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ color: colors.grey[100], mb: 1 }}>
              {t('consultation.observation')}
            </Typography>
            <TextField
              multiline
              rows={4}
              fullWidth
              variant="outlined"
              value={observation}
              onChange={(e) => setObservation(e.target.value)}
              sx={{
                bgcolor: '#EEF0F3',
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': { borderColor: colors.greenAccent[500] },
                },
              }}
            />
            <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleSaveObservation}
                sx={{
                  bgcolor: colors.greenAccent[600],
                  '&:hover': { bgcolor: colors.greenAccent[700] },
                }}
              >
                {t('buttons.saveObservation')}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default HistoryModal;
